import React, { useContext } from "react";
import {
    createValidationResult,
    ensure,
    FormDialog,
    FormDropdown,
    FormFields,
    FormTextField,
    FormValidationSummary,
    mapToDropdownOption,
    ShimmeredDetailsList,
    Spinner,
    Stack,
    useNotifications,
    usePerformMultipleActions
} from "sprint-common-web";

import { useFileRecordApi } from "../../../../common/api/master-api/file-records/useFileRecordApi";
import { FileRecordStatus } from "../../../../common/types/FileRecordStatus";
import { canFileRecordBeChangedToRejected } from "../../common/utils/validateFileRecordStatus";
import { CourseOfActionFormFields } from "../../forms/status/status-details/course-of-action/CourseOfActionFormFields";
import { useCourseOfActionFormFieldsValues } from "../../forms/status/status-details/course-of-action/useCourseOfActionFormFieldsValues";
import { FileRecordRejectionReasonFormInputs } from "../../reject/FileRecordRejectionReasonFormInputs";
import { useFileRecordRejectionReasonForm } from "../../reject/useFileRecordRejectionReasonForm";
import { validateDiscardedFileRecords } from "../buttons/update-multiple-status/utils/validateDiscardedFileRecords";
import { FileRecordListingContext } from "../context/FileRecordListingContext";
import { createRejectFileRecordListingColumns } from "./createRejectFileRecordListingColumns";
import { mapToRejectFileRecordListingItem, RejectFileRecordListingItem } from "./RejectFileRecordListingItem";

type RejectMultipleFileRecordsDialogProps = {
    onClose(): void;
};

export const RejectMultipleFileRecordsDialog: React.VFC<RejectMultipleFileRecordsDialogProps> = ({
    onClose
}: RejectMultipleFileRecordsDialogProps) => {
    const { rejectFileRecord } = useFileRecordApi();
    const { rejectFileRecord: rejectItem, selectedItems, updateFileRecordStatus } = useContext(
        FileRecordListingContext
    );
    const {
        control,
        errors,
        formState,
        handleSubmit,
        reset,
        areRejectionReasonsLoading,
        rejectionReasons
    } = useFileRecordRejectionReasonForm();
    const {
        courseOfActions,
        fixedBillings,
        areCourseOfActionsLoading,
        areFixedBillingsLoading
    } = useCourseOfActionFormFieldsValues();
    const { success, error: errorNotification } = useNotifications();
    const { performAllItems, isActionInProgress, items } = usePerformMultipleActions<RejectFileRecordListingItem>({
        isAvailable: (item) => canFileRecordBeChangedToRejected(item.status),
        selectedItems: selectedItems.map(mapToRejectFileRecordListingItem)
    });

    const formHasErrors = createValidationResult([
        validateDiscardedFileRecords(items, selectedItems.length, FileRecordStatus.Rejected)
    ]);
    const areValidOrdersEmpty = !items.length;

    const onCancel = () => {
        onClose();
        reset();
    };

    const reject = async (inputs: FileRecordRejectionReasonFormInputs) => {
        const results = await rejectAllItems(inputs);

        if (results.every(({ status }) => status === "fulfilled")) {
            onCancel();

            success("Rejecting complete");
        } else errorNotification("Something went wrong while rejecting orders");
    };

    const rejectAllItems = async (inputs: FileRecordRejectionReasonFormInputs) => {
        const rejectionReason = rejectionReasons.find((reason) => reason.id === inputs.reasonId);
        const courseOfAction = courseOfActions.find((action) => action.id === inputs.courseOfActionId);
        const fixedBilling = inputs.fixedBillingId
            ? fixedBillings.find((billing) => billing.id === inputs.fixedBillingId)
            : undefined;

        ensure(courseOfAction);

        return performAllItems(async (item) => {
            await rejectFileRecord({
                fileRecordId: item.id,
                courseOfActionId: inputs.courseOfActionId,
                fixedBillingId: inputs.fixedBillingId,
                reasonId: inputs.reasonId,
                details: inputs.details
            });

            updateFileRecordStatus(item.id, FileRecordStatus.Rejected, {
                courseOfAction: courseOfAction.name,
                fixedBilling: fixedBilling?.name,
                reason: rejectionReason?.name,
                details: inputs.details
            });

            rejectItem(item.id);
        });
    };

    const columns = createRejectFileRecordListingColumns();

    return (
        <FormDialog
            title={"Reject file records"}
            formState={formState}
            text={"Are you sure you want to start rejecting listed file records?"}
            isBlocking={isActionInProgress}
            showCloseButton={false}
            submit={{
                onClick: handleSubmit(reject),
                disabled:
                    areValidOrdersEmpty ||
                    isActionInProgress ||
                    areRejectionReasonsLoading ||
                    areCourseOfActionsLoading ||
                    areFixedBillingsLoading
            }}
            cancel={{
                onClick: onCancel
            }}
            width={"65%"}
            contentHeight={500}
        >
            {areRejectionReasonsLoading || areCourseOfActionsLoading || areFixedBillingsLoading ? (
                <Stack
                    styles={{
                        root: { padding: 10 }
                    }}
                >
                    <Spinner />
                </Stack>
            ) : (
                <>
                    {items.length > 0 && (
                        <FormFields>
                            <FormDropdown
                                name="reasonId"
                                label="Reason"
                                options={rejectionReasons.map((reason) => mapToDropdownOption(reason))}
                                rules={{ required: "Select a reason" }}
                                control={control}
                                errors={errors}
                            />
                            <CourseOfActionFormFields
                                control={control}
                                errors={errors}
                                courseOfActions={courseOfActions}
                                fixedBillings={fixedBillings}
                            />
                            <FormTextField
                                name="details"
                                label="Details"
                                control={control}
                                errors={errors}
                                multiline={true}
                            />
                        </FormFields>
                    )}
                    <FormValidationSummary validationResult={formHasErrors} />
                    {items.length > 0 && (
                        <ShimmeredDetailsList columns={columns} items={items} layoutMode="justified" />
                    )}
                </>
            )}
        </FormDialog>
    );
};
